/**
 * `match_reveal` renderer — the final "Meet your first match" screen with
 * a full-bleed portrait card, typewriter-animated chat bubble, and handoff
 * CTA. Used by the SpicyChat onboarding funnel (step 10, NX-9562).
 *
 * Step JSON shape:
 *   {
 *     "type": "match_reveal",
 *     "data": {
 *       "characterName": "Aria",
 *       "characterTag": "Romantic · AI Companion",
 *       "characterImageUrl": "https://…",
 *       "badge": "Best match",
 *       "message": "Hey {name|there} — I've been waiting for you…",
 *       "avatarUrl": "https://…",
 *       "ctaLabel": "Start chat",
 *       "secondaryLabel": "See other matches"
 *     }
 *   }
 */
import { MatchReveal } from "@nextday/funnel-ui";
import { interpolate } from "@nextday/funnel-ui/interpolate";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

export const matchRevealSchema = z
  .object({
    eyebrow: z.string().optional(),
    headline: z.string().optional(),
    description: z.string().optional(),
    characterName: z.string(),
    characterTag: z.string().optional(),
    characterImageUrl: z.string(),
    badge: z.string().optional(),
    message: z.string(),
    avatarUrl: z.string().optional(),
    ctaLabel: z.string().optional(),
    secondaryLabel: z.string().optional(),
    restartLabel: z.string().optional(),
    typingMs: z.number().int().nonnegative().optional(),
    nameAnswerKey: z.string().optional(),
  })
  .strict();

export type MatchRevealData = z.infer<typeof matchRevealSchema>;

const MatchRevealRenderer = ({
  data,
  step,
  answers,
  onAnswer,
  onAdvance,
  onRestart,
}: RendererProps<MatchRevealData>) => {
  const nameKey = data.nameAnswerKey ?? "name";
  const name = typeof answers[nameKey] === "string" ? answers[nameKey] : "";
  const characterName = data.characterName || "Your match";
  const message = data.message || "Hey there — ready to chat?";
  const resolvedMessage = interpolate(message, { name });

  const handleCta = (): void => {
    onAnswer("match_reveal_action", { value: "start_chat" });
    onAdvance();
  };

  const handleSecondary = (): void => {
    onAnswer("match_reveal_action", { value: "see_others" });
    onAdvance();
  };

  return (
    <MatchReveal
      eyebrow={data.eyebrow}
      headline={data.headline}
      description={data.description}
      characterName={characterName}
      characterTag={data.characterTag}
      characterImageUrl={data.characterImageUrl || undefined}
      badge={data.badge}
      message={resolvedMessage}
      avatarUrl={data.avatarUrl}
      ctaLabel={data.ctaLabel}
      onCta={handleCta}
      onPress={handleCta}
      secondaryLabel={data.secondaryLabel}
      onSecondary={data.secondaryLabel ? handleSecondary : undefined}
      restartLabel={data.restartLabel}
      onRestart={onRestart}
      typingMs={data.typingMs}
      testId={`renderer-match-reveal-${step.id}`}
    />
  );
};

export const matchRevealRenderer = createRenderer({
  type: "match_reveal",
  dataSchema: matchRevealSchema,
  label: "Match reveal",
  Component: MatchRevealRenderer,
});

export default matchRevealRenderer;
